import { AxiosInstance, AxiosRequestConfig } from "axios";
import { useAuthStore } from "@/features/auth/useAuthStore";
import { createHttpInstance } from "@/features/core-shared/utils/http/http";
import { SupplierFull, AccountManager } from "@/types/ContractMasterData";
import { SupplierLegalDocumentHistory, LegalDocumentType, LegalDocumentDownloadUrl } from "@/types/ContractMasterData";
import type { InviteUserRequest } from "@/types/generated/ContractMasterDataApiV2";

const CONTRACT_API_VERSION = "vnd.contract-master-data-offer-service.v2";

type DocumentType = "liability_insurance" | "business_license" | "bank_certificate" | "bank_certificate_opt";

interface Options {
  config: AxiosRequestConfig;
}

function getSuppliers(http: AxiosInstance) {
  return async (
    filters?: Record<string, any>,
    options?: Options & { limit?: number; offset?: number; sort?: string; query?: string }
  ) => {
    const { config, limit, offset, sort, query } = options || {};
    const params = {
      ...(config?.params || {}),
      ...(limit !== undefined && { limit }),
      ...(offset !== undefined && { offset }),
      ...(sort && { sort }),
      ...(filters?.filters && { filters: filters.filters }),
    };

    if (query?.trim()) {
      const trimmedQuery = query.trim();
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmedQuery);

      // UUID searches have be to handled separately with exact search per backend requirement
      let searchFilter;
      if (isUuid) {
        searchFilter = `id=="${trimmedQuery}"`;
      } else {
        // Backslash escapes special characters so MongoDB treats them as literal text, not regex operators
        const escapedQuery = trimmedQuery.replace(/[.?+*|{}[\]()<>"\\@#]/g, "\\$&");
        searchFilter = `(company_details.company_name=re="${escapedQuery}" or contact_information.email=re="${escapedQuery}")`;
      }

      params.filters = [filters?.filters, searchFilter].filter(Boolean).join(";");
    }

    const { data, headers } = await http.get<SupplierFull[]>(`/suppliers`, {
      ...config,
      params,
      headers: { "accept-version": CONTRACT_API_VERSION },
    });
    return { data, headers };
  };
}

function getSupplierById(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.get<SupplierFull>(`/suppliers/${id}`, {
      ...config,
      headers: {
        ...config?.headers,
        "accept-version": CONTRACT_API_VERSION,
      },
    });

    return { data };
  };
}

function getSupplierLegalDocuments(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<SupplierLegalDocumentHistory[]>(`/suppliers/${id}/users/documents`, {
      ...config,
      headers: {
        ...config?.headers,
        "accept-version": CONTRACT_API_VERSION,
      },
    });
    return { data };
  };
}

function getLegalDocumentDownloadUrl(http: AxiosInstance) {
  return async (documentType: LegalDocumentType, version?: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<LegalDocumentDownloadUrl>(`/documents`, {
      params: {
        doc_type: documentType,
        ...(version && { version }),
      },
      ...config,
      headers: {
        ...config?.headers,
        "accept-version": CONTRACT_API_VERSION,
      },
    });
    return { data };
  };
}

function updateSupplierById(http: AxiosInstance) {
  return async (id: string, supplier: Partial<SupplierFull>, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.patch<SupplierFull>(`/suppliers/${id}`, supplier, {
      ...config,
      headers: {
        ...config?.headers,
        "accept-version": CONTRACT_API_VERSION,
      },
    });
    return { data };
  };
}

function verifySupplierById(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.put<{}>(
      `/suppliers/${id}/profile-verified`,
      {},
      {
        ...config,
        headers: {
          ...config?.headers,
          "accept-version": CONTRACT_API_VERSION,
        },
      }
    );
    return { data };
  };
}

function unlockSupplierById(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.put<{}>(
      `/suppliers/${id}/unlock-editing`,
      {},
      {
        ...config,
        headers: {
          ...config?.headers,
          "accept-version": CONTRACT_API_VERSION,
        },
      }
    );
    return { data };
  };
}

function setSupplierTypeInternal(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.put<SupplierFull>(
      `/suppliers/${id}/type/internal`,
      {},
      {
        ...config,
        headers: {
          ...config?.headers,
          "accept-version": CONTRACT_API_VERSION,
        },
      }
    );
    return { data };
  };
}

function getAccountManagers(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<AccountManager[]>(`/suppliers/account-managers`, config);
    return { data };
  };
}

function getSupplierDocumentDownloadUrl(http: AxiosInstance) {
  return async (supplierId: string, documentType: DocumentType, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get(`/suppliers/${supplierId}/documents`, {
      ...config,
      params: {
        document_type: documentType,
        action: "download",
        ...config?.params,
      },
      headers: {
        ...config?.headers,
        "accept-version": CONTRACT_API_VERSION,
      },
    });
    return { data };
  };
}

function getSupplierDocumentUploadUrl(http: AxiosInstance) {
  return async (supplierId: string, documentType: DocumentType, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get(`/suppliers/${supplierId}/documents/uploads`, {
      ...config,
      params: {
        document_type: documentType,
        ...config?.params,
      },
      headers: {
        ...config?.headers,
        "accept-version": CONTRACT_API_VERSION,
      },
    });
    return { data };
  };
}

export type { InviteUserRequest };

function inviteSupplierAccount(http: AxiosInstance) {
  return async (supplierId: string, payload: InviteUserRequest, options?: Options) => {
    const { config } = options ?? {};
    const { data } = await http.post(`/suppliers/${supplierId}/invite`, payload, {
      ...config,
      headers: {
        ...config?.headers,
        "accept-version": CONTRACT_API_VERSION,
        "Content-Type": "application/json",
      },
    });
    return { data };
  };
}

function uploadSupplierDocument(http: AxiosInstance) {
  return async (supplierId: string, documentType: DocumentType, file: File) => {
    const getUploadUrl = getSupplierDocumentUploadUrl(http);
    const { data: uploadData } = await getUploadUrl(supplierId, documentType);

    if (!uploadData?.url) {
      throw new Error("No upload URL received from backend");
    }

    try {
      const response = await fetch(uploadData.url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      return { data: { success: true } };
    } catch (error) {
      if (error instanceof TypeError) {
        throw new Error("Network error occurred during file upload");
      }
      throw error;
    }
  };
}

/**
 * Contract Master Data Api
 *
 * openapi file: contract-master-data-openapi.yaml
 */
export function useContractMasterDataApi() {
  const runtimeConfig = useRuntimeConfig();
  const auth = useAuthStore();

  const axiosInstance = createHttpInstance({
    config: {
      baseURL: runtimeConfig.public.CONTRACT_MASTER_DATA_SERVICE_BASE_URL,
      headers: { "accept-version": "vnd.contract-master-data-offer-service.v2" },
    },
    auth,
  });

  return {
    _axiosInstance: axiosInstance,
    getSuppliers: getSuppliers(axiosInstance),
    getSupplierById: getSupplierById(axiosInstance),
    updateSupplierById: updateSupplierById(axiosInstance),
    getAccountManagers: getAccountManagers(axiosInstance),
    getSupplierDocumentDownloadUrl: getSupplierDocumentDownloadUrl(axiosInstance),
    getSupplierDocumentUploadUrl: getSupplierDocumentUploadUrl(axiosInstance),
    uploadSupplierDocument: uploadSupplierDocument(axiosInstance),
    verifySupplierById: verifySupplierById(axiosInstance),
    unlockSupplierById: unlockSupplierById(axiosInstance),
    setSupplierTypeInternal: setSupplierTypeInternal(axiosInstance),
    getSupplierLegalDocuments: getSupplierLegalDocuments(axiosInstance),
    getLegalDocumentDownloadUrl: getLegalDocumentDownloadUrl(axiosInstance),
    inviteSupplierAccount: inviteSupplierAccount(axiosInstance),
  };
}

export type UseContractMasterDataApi = ReturnType<typeof useContractMasterDataApi>;
